import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { S, formatDate } from '../utils/styles';

export default function Dispatched() {
  const [shipments, setShipments] = useState([]);
  const [miscSales, setMiscSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingShipment, setEditingShipment] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editDate, setEditDate] = useState('');
  const [editContainers, setEditContainers] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/orders/shipments/all'),
      api.get('/inventory/misc-sales')
    ]).then(([s, m]) => {
      setShipments(s.data);
      setMiscSales(m.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startEdit = async (s) => {
    try {
      const { data } = await api.get(`/orders/${s.order_id}/shipments/${s.id}/items`);
      setEditItems(data.map(i => ({ ...i, new_qty: String(i.qty_shipped) })));
      setEditDate(s.dispatch_date || '');
      setEditContainers(String(s.containers_count || 0));
      setEditNotes(s.notes || '');
      setEditingShipment(s);
    } catch(e) { alert('Error loading shipment items'); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/orders/${editingShipment.order_id}/shipments/${editingShipment.id}`, {
        dispatch_date: editDate,
        notes: editNotes,
        containers_count: parseInt(editContainers) || 0,
        items: editItems.map(i => ({ order_item_id: i.order_item_id, qty_shipped: parseInt(i.new_qty) || 0 }))
      });
      setEditingShipment(null);
      load();
    } catch(e) { alert(e.response?.data?.error || 'Error saving'); }
    finally { setSaving(false); }
  };

  const filtered = shipments.filter(s =>
    s.pi_number?.toLowerCase().includes(search.toLowerCase()) ||
    s.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalValue = filtered.reduce((sum, s) => sum + (s.value_usd || 0), 0);
  const totalPcs = filtered.reduce((sum, s) => sum + (s.total_pcs || 0), 0);
  const totalContainers = filtered.reduce((sum, s) => sum + (s.containers_count || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Dispatched Orders</h1>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>All shipments — click ✎ Edit to correct quantities, date, containers or notes</div>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PI number or customer..." style={{ ...S.input, width: 280, marginBottom: 16 }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total dispatched value', value: `$${totalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: '#3b82f6' },
          { label: 'Total pcs shipped', value: totalPcs.toLocaleString(), color: '#10b981' },
          { label: 'Total containers', value: totalContainers, color: '#8b5cf6' },
        ].map(c => (
          <div key={c.label} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 18px', borderLeft:`4px solid ${c.color}` }}>
            <div style={{ fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.6, marginBottom:4 }}>{c.label}</div>
            <div style={{ fontSize:22, fontWeight:700 }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['Dispatch date','PI Number','Customer','Pcs shipped','Value','Containers','Notes',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>Loading...</td></tr>}
            {!loading && filtered.map(s => (
              <tr key={s.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={{ ...S.td, fontWeight:600 }}>{formatDate(s.dispatch_date)}</td>
                <td style={S.td}><Link to={`/orders/${s.order_id}`} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none' }}>{s.pi_number}</Link></td>
                <td style={S.td}>{s.company_name}</td>
                <td style={S.td}>{(s.total_pcs||0).toLocaleString()}</td>
                <td style={{ ...S.td, fontWeight:600, color:'#10b981' }}>${(s.value_usd||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td style={{ ...S.td, color: s.containers_count > 0 ? '#374151' : '#ef4444' }}>{s.containers_count || <span style={{ color:'#ef4444' }}>0 — edit to set</span>}</td>
                <td style={{ ...S.td, color:'#9ca3af', fontSize:12 }}>{s.notes||'—'}</td>
                <td style={{ padding:'6px 10px' }}>
                  <button onClick={() => startEdit(s)} style={{ ...S.btnSm, background:'#eff6ff', color:'#3b82f6', fontSize:11 }}>✎ Edit</button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>No dispatches recorded yet</td></tr>}
            {!loading && filtered.length > 0 && (
              <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                <td colSpan={3} style={{ ...S.td, fontWeight:700 }}>Total ({filtered.length} shipments)</td>
                <td style={{ ...S.td, fontWeight:700 }}>{totalPcs.toLocaleString()}</td>
                <td style={{ ...S.td, fontWeight:700, color:'#10b981' }}>${totalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td style={{ ...S.td, fontWeight:700 }}>{totalContainers}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Misc Dead Stock Sales */}
      {miscSales.length > 0 && (
        <div style={{ marginTop:28 }}>
          <div style={{ fontWeight:600, fontSize:16, marginBottom:4 }}>Misc Dead Stock Sales</div>
          <div style={{ fontSize:13, color:'#6b7280', marginBottom:12 }}>Finished goods sold outside normal orders</div>
          <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Date','Product','Size','Qty','Price/pc','Total Value','Notes'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {miscSales.map(s => (
                  <tr key={s.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, fontSize:12 }}>{formatDate(s.sale_date)}</td>
                    <td style={{ ...S.td, color:'#3b82f6', fontWeight:600 }}>{s.product_code}</td>
                    <td style={S.td}>{s.size}</td>
                    <td style={{ ...S.td, fontWeight:500 }}>{(s.qty||0).toLocaleString()} pcs</td>
                    <td style={{ ...S.td, color:'#6b7280' }}>${(s.price_per_unit||0).toFixed(4)}</td>
                    <td style={{ ...S.td, fontWeight:600, color:'#10b981' }}>${(s.total_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={{ ...S.td, color:'#9ca3af', fontSize:12 }}>{s.notes||'—'}</td>
                  </tr>
                ))}
                <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                  <td colSpan={5} style={{ ...S.td, fontWeight:700 }}>Total ({miscSales.length} sales)</td>
                  <td style={{ ...S.td, fontWeight:700, color:'#10b981' }}>
                    ${miscSales.reduce((s,m) => s+(m.total_value||0), 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Shipment Modal */}
      {editingShipment && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:700, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>Edit dispatch — {editingShipment.pi_number}</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>Changes to quantities update stock automatically.</div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
              <div>
                <label style={S.label}>Dispatch date</label>
                <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Containers</label>
                <input type="number" value={editContainers} onChange={e => setEditContainers(e.target.value)} style={S.input} min="0" />
              </div>
              <div>
                <label style={S.label}>Notes</label>
                <input value={editNotes} onChange={e => setEditNotes(e.target.value)} style={S.input} />
              </div>
            </div>

            <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Shipped quantities</div>
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
              <thead><tr>{['Product','Size','Original qty','New qty'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {editItems.map((item, i) => (
                  <tr key={item.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, color:'#3b82f6', fontWeight:600 }}>{item.product_code||'—'}</td>
                    <td style={S.td}>{item.size}</td>
                    <td style={{ ...S.td, color:'#9ca3af' }}>{item.qty_shipped}</td>
                    <td style={{ padding:'5px 8px' }}>
                      <input type="number" value={item.new_qty}
                        onChange={e => setEditItems(arr => arr.map((it,idx) => idx===i ? {...it, new_qty: e.target.value} : it))}
                        style={{ ...S.input, width:100, fontSize:13 }} min="0" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'8px 12px', marginBottom:16, fontSize:12, color:'#92400e' }}>
              ⚠️ Changing quantities will adjust order_items.qty_shipped accordingly. Only correct actual dispatch mistakes here.
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={saveEdit} disabled={saving} style={S.btnPrimary}>{saving ? 'Saving...' : 'Save changes'}</button>
              <button onClick={() => setEditingShipment(null)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
